import {
  CurrentIncentivesResultTypenames,
  IncentiveAward,
  IncentiveCategory,
  IncentiveFulfillmentStatus,
  IncentiveLifecycleStatus,
  IncentiveProgram,
  IncentiveProgramStatus,
  IncentiveProgress,
  IncentiveProgressResultTypenames,
  IncentiveProgressSource,
  IncentiveProgressUnit,
  IncentiveStatus,
  IncentiveStatusResultTypenames,
  Mutation,
  OptInToIncentiveResultTypenames,
  Query,
} from '@varo/graphql-schema-types';
import { MockHandlers } from 'services/UrqlClient/exchanges/mockExchange/utils/mockUtils';
import { Operation } from 'urql';

export const MOCK_INCENTIVE_PROGRAM_ID = 'mock-sweepstakes-program';
export const MOCK_INCENTIVE_NO_BELIEVE_PROGRAM_ID =
  'mock-sweepstakes-program-no-card';
export const MOCK_INCENTIVE_ERROR_PROGRAM_ID = 'mock-incentive-error';
export const MOCK_DEFAULT_LIFECYCLE_STATUS = IncentiveLifecycleStatus.Targeted;

const MOCK_ERROR = {
  code: 'MOCK_ERROR',
  title: 'Something went wrong',
  message: 'Please try again later.',
};

const OPTED_IN_STATUSES = [
  IncentiveLifecycleStatus.OptInPendingEligibility,
  IncentiveLifecycleStatus.Enrolled,
  IncentiveLifecycleStatus.InProgress,
  IncentiveLifecycleStatus.Qualified,
  IncentiveLifecycleStatus.Expired,
];

export const mockIncentiveProgram: IncentiveProgram = {
  __typename: 'IncentiveProgram',
  id: MOCK_INCENTIVE_PROGRAM_ID,
  incentiveType: IncentiveCategory.Sweepstakes,
  name: 'Believe Holiday Sweepstakes 2026',
  programContent: JSON.stringify({
    intro:
      'Use your Believe secured card for everyday purchases of $10 or more to earn chances to win cash. The more you use your card, the more chances you get to win.\n\nThe Grand Giveaway runs from November 9, 2026 to January 25, 2027.',
    entry_tiers_title: 'Get chances with every purchase',
    entry_tiers_description:
      'The amount you spend determines how many chances you get.',
    entry_tiers_display: [
      { label: '$10 to $24.99', chances: '1 chance' },
      { label: '$25 or more', chances: '2 chances' },
    ],
    illustration: 'CASH_F',
    prize_schedule: [
      { date: '2026-11-18', amount_display: '1000.00', winner_count: 5 },
      { date: '2026-12-02', amount_display: '1000.00', winner_count: 5 },
      { date: '2026-12-16', amount_display: '1000.00', winner_count: 5 },
      {
        date: '2026-12-16',
        label: 'Grand prize',
        amount_display: '5000.00',
        winner_count: 1,
      },
      { date: '2027-01-06', amount_display: '1000.00', winner_count: 5 },
    ],
  }),
  programContentSchemaVersion: 'v1',
  status: IncentiveProgramStatus.Active,
  windowStart: '2026-11-09',
  windowEnd: '2027-01-25',
  termsAndConditionsVersion: 'v1',
  termsAndConditionsUrl: 'https://varo.com/legal/sweeps-2026-v1',
};

export const mockIncentiveAward: IncentiveAward = {
  __typename: 'IncentiveAward',
  id: 'mock-incentive-award',
  amount: {
    __typename: 'Money',
    amount: '5000.00',
    currency: 'USD',
    formatted: '$5,000.00',
    formattedWithPlusPrefix: '+$5,000.00',
    formattedWithPlusPrefixOrAbs: '+$5,000.00',
  },
  drawingId: 'mock-incentive-drawing',
  drawingAwardId: 'mock-incentive-drawing-award',
  determinedAt: '2026-12-16T20:00:00Z',
  fulfillmentStatus: IncentiveFulfillmentStatus.Fulfilled,
  fulfilledAt: '2026-12-17T18:42:11Z',
};

export const createMockIncentiveStatus = (
  status: IncentiveLifecycleStatus,
  incentiveProgramId = MOCK_INCENTIVE_PROGRAM_ID,
): IncentiveStatus => {
  const isOptedIn = OPTED_IN_STATUSES.includes(status);

  return {
    __typename: 'IncentiveStatus',
    id: incentiveProgramId,
    status,
    optedInAt: isOptedIn ? '2026-11-10T15:30:00Z' : null,
    measurementStart: isOptedIn ? '2026-11-09T00:00:00Z' : null,
    measurementEnd: isOptedIn ? '2027-01-25T23:59:59Z' : null,
    awards:
      status === IncentiveLifecycleStatus.Qualified ? [mockIncentiveAward] : [],
  };
};

export const mockIncentiveProgress: IncentiveProgress = {
  __typename: 'IncentiveProgress',
  id: MOCK_INCENTIVE_PROGRAM_ID,
  cumulativeValue: '7.00',
  effectiveAt: '2026-11-14T00:00:00Z',
  progressValues: [
    {
      __typename: 'IncentiveProgressValue',
      sourceTransactionId: 'mock-progress-transaction-1',
      value: '1.00',
      valueType: IncentiveProgressUnit.Entries,
      progressType: IncentiveProgressSource.Transaction,
      occurredAt: '2026-11-10T18:42:11Z',
    },
    {
      __typename: 'IncentiveProgressValue',
      sourceTransactionId: 'mock-progress-transaction-2',
      value: '2.00',
      valueType: IncentiveProgressUnit.Entries,
      progressType: IncentiveProgressSource.Transaction,
      occurredAt: '2026-11-12T16:05:29Z',
    },
    {
      __typename: 'IncentiveProgressValue',
      sourceTransactionId: null,
      value: '5.00',
      valueType: IncentiveProgressUnit.Entries,
      progressType: IncentiveProgressSource.Bonus,
      occurredAt: '2026-11-13T09:00:00Z',
    },
    {
      __typename: 'IncentiveProgressValue',
      sourceTransactionId: 'mock-progress-transaction-1',
      value: '-1.00',
      valueType: IncentiveProgressUnit.Entries,
      progressType: IncentiveProgressSource.Reversal,
      occurredAt: '2026-11-14T11:20:00Z',
    },
  ],
};

const getIncentiveProgramId = (operation: Operation): string => {
  const input: unknown = operation.variables?.input;

  return typeof input === 'object' &&
    input !== null &&
    'incentiveProgramId' in input &&
    typeof input.incentiveProgramId === 'string'
    ? input.incentiveProgramId
    : MOCK_INCENTIVE_PROGRAM_ID;
};

const resolveMockLifecycleStatus = (
  incentiveProgramId: string,
): IncentiveLifecycleStatus =>
  Object.values(IncentiveLifecycleStatus).find(
    (status) => status === incentiveProgramId,
  ) ?? MOCK_DEFAULT_LIFECYCLE_STATUS;

const optedInProgramIds = new Set<string>();

const resolveOptedInStatus = (incentiveProgramId: string) =>
  incentiveProgramId === MOCK_INCENTIVE_PROGRAM_ID
    ? IncentiveLifecycleStatus.Enrolled
    : IncentiveLifecycleStatus.OptInPendingEligibility;

export const resetMockIncentiveOptIns = () => {
  optedInProgramIds.clear();
};

const getOptInRank = (incentiveProgramId: string) => {
  const optInOrder = [...optedInProgramIds].reverse();
  const rank = optInOrder.indexOf(incentiveProgramId);

  return rank === -1 ? optInOrder.length : rank;
};

export const mockIncentiveHandlers: MockHandlers = {
  CurrentIncentives: (): Pick<Query, 'currentIncentives'> => ({
    currentIncentives: {
      __typename: CurrentIncentivesResultTypenames.CurrentIncentivesSuccess,
      // Most recent opt-in first so the account card picks the program the user joined.
      incentives: [
        mockIncentiveProgram,
        {
          ...mockIncentiveProgram,
          id: MOCK_INCENTIVE_NO_BELIEVE_PROGRAM_ID,
        },
        ...Object.values(IncentiveLifecycleStatus).map((status) => ({
          ...mockIncentiveProgram,
          id: status,
        })),
      ].sort(
        (first, second) => getOptInRank(first.id) - getOptInRank(second.id),
      ),
    },
  }),
  IncentiveStatus: (operation): Pick<Query, 'incentiveStatus'> => {
    const incentiveProgramId = getIncentiveProgramId(operation);

    if (incentiveProgramId === MOCK_INCENTIVE_ERROR_PROGRAM_ID) {
      return {
        incentiveStatus: {
          __typename: IncentiveStatusResultTypenames.IncentiveStatusError,
          ...MOCK_ERROR,
        },
      };
    }

    return {
      incentiveStatus: {
        __typename: IncentiveStatusResultTypenames.IncentiveStatusSuccess,
        status: createMockIncentiveStatus(
          optedInProgramIds.has(incentiveProgramId)
            ? resolveOptedInStatus(incentiveProgramId)
            : resolveMockLifecycleStatus(incentiveProgramId),
          incentiveProgramId,
        ),
      },
    };
  },
  IncentiveProgress: (operation): Pick<Query, 'incentiveProgress'> => {
    const incentiveProgramId = getIncentiveProgramId(operation);

    if (incentiveProgramId === MOCK_INCENTIVE_ERROR_PROGRAM_ID) {
      return {
        incentiveProgress: {
          __typename: IncentiveProgressResultTypenames.IncentiveProgressError,
          ...MOCK_ERROR,
        },
      };
    }

    return {
      incentiveProgress: {
        __typename: IncentiveProgressResultTypenames.IncentiveProgressSuccess,
        progress: { ...mockIncentiveProgress, id: incentiveProgramId },
      },
    };
  },
  OptInToIncentive: (operation): Pick<Mutation, 'optInToIncentive'> => {
    const incentiveProgramId = getIncentiveProgramId(operation);

    if (incentiveProgramId === MOCK_INCENTIVE_ERROR_PROGRAM_ID) {
      return {
        optInToIncentive: {
          __typename: OptInToIncentiveResultTypenames.OptInToIncentiveError,
          ...MOCK_ERROR,
        },
      };
    }

    optedInProgramIds.add(incentiveProgramId);

    return {
      optInToIncentive: {
        __typename: OptInToIncentiveResultTypenames.OptInToIncentiveSuccess,
        status: createMockIncentiveStatus(
          resolveOptedInStatus(incentiveProgramId),
          incentiveProgramId,
        ),
      },
    };
  },
};
